import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
} from "@win-reality/react-ui";
import { useDispatch, useSelector } from "react-redux";

import { TILE_OPTIONS, type TileOption } from "../../../constants/tile-options";
import { setTrackingState } from "../../../store/slices/socket";
import { setModalState } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import type { ITileConfig, VisualizationType } from "../../../store/types";
import {
  type TileTypeKey,
  toggleTileVisibility,
  updateTile,
} from "../../../utils/tile-helpers";
import { TileSelector } from "./TileSelector";

interface QuickTileChangeModalProps {
  excludeVisualizations?: boolean;
}

export const QuickTileChangeModal = ({
  excludeVisualizations,
}: QuickTileChangeModalProps = {}) => {
  const dispatch = useDispatch();
  const { state } = useSelector((state: RootState) => state.view.modalState);
  const quickChanger = useSelector(
    (state: RootState) => state.view.quickTileChange,
  );
  const tilesState = useSelector((state: RootState) => state.tiles);

  const handleClose = () => {
    dispatch(setModalState({ state: null, data: {} }));
    dispatch(setTrackingState(true));
  };

  const handleTileChange = (
    selectedValue: string,
    selectedOption: TileOption,
  ) => {
    const isVisualization = selectedOption.type === "visualization";

    const newTileConfig: ITileConfig = isVisualization
      ? {
          type: "visualization",
          visualization: selectedValue as VisualizationType,
          hidden: false,
        }
      : {
          type: "data",
          title: selectedValue,
          hidden: false,
        };

    updateTile(
      quickChanger.tileType as TileTypeKey,
      quickChanger.tileKey,
      newTileConfig,
      tilesState,
      dispatch,
    );

    handleClose();
  };

  const handleHideTile = () => {
    toggleTileVisibility(
      quickChanger.tileType as TileTypeKey,
      quickChanger.tileKey,
      true,
      tilesState,
      dispatch,
    );
    handleClose();
  };

  return (
    <Dialog
      open={state === "Tile Quick Change"}
      onOpenChange={(open) => !open && handleClose()}
      defaultCancelButton={false}
      size="sm"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>Tile Quick Change</DialogTitle>
            </DialogHeader>

            <TileSelector
              data={
                excludeVisualizations
                  ? TILE_OPTIONS.filter((opt) => opt.type !== "visualization")
                  : TILE_OPTIONS
              }
              tileType={quickChanger.tileType as TileTypeKey}
              label={quickChanger.label}
              placeholder={quickChanger.placeholder}
              onChange={handleTileChange}
            />
            <Button
              variant="secondary-flat-dark"
              size="sm"
              className="mt-4 w-full"
              onClick={handleHideTile}
            >
              Hide This Tile
            </Button>
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-start">
              <Button
                variant="ghost"
                onClick={handleClose}
                className="underline"
              >
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
